import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Common PDF Initialization
const initPdf = (title) => {
  const doc = new jsPDF();
  
  // Brand Header
  doc.setFillColor(147, 51, 234); // Purple-600
  doc.rect(0, 0, 220, 30, 'F');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(22);
  doc.setFont("helvetica", "bold");
  doc.text("FurpectTails", 14, 20);

  // Report Title
  doc.setTextColor(50, 50, 50);
  doc.setFontSize(16);
  doc.text(title, 14, 45);

  // Date
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 52);

  return doc;
};

// 1. Sales Report
export const generateSalesReport = (appointments, dateRangeInfo = {}) => {
  const periodText = dateRangeInfo.periodLabel || "All Time";
  const doc = initPdf("Sales & Appointments Report");

  // Add Period info
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 120);
  doc.text(`Report Period: ${periodText}`, 14, 52);
  doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 58);

  // Calculate totals
  const totalRevenue = appointments
    .filter(a => a.status === "completed")
    .reduce((sum, a) => sum + (a.totalPrice || 0), 0);
  
  const completedCount = appointments.filter(a => a.status === "completed").length;
  const cancelledCount = appointments.filter(a => a.status === "cancelled").length;
  
  doc.setFontSize(11);
  doc.setTextColor(0, 128, 0);
  doc.text(`Total Revenue (Completed): P${totalRevenue.toLocaleString()}`, 14, 68);
  doc.setTextColor(50, 50, 50);
  doc.text(`Total Appointments: ${appointments.length} (${completedCount} Completed, ${cancelledCount} Cancelled)`, 14, 75);

  // Table Data
  const tableColumn = ["Date", "Customer", "Pet", "Service", "Status", "Payment", "Price"];
  const tableRows = [];

  appointments.forEach(a => {
    const dateSource = a.pets?.[0]?.appointmentDate || a.createdAt;
    const aptDate = dateSource ? new Date(dateSource).toLocaleDateString("en-US", { year: 'numeric', month: 'short', day: 'numeric' }) : "N/A";
    const customer = a.ownerName || "N/A";
    const pet = a.pets?.map(p => p.petName).join(", ") || "N/A";
    const service = a.pets?.map(p => p.serviceType).join(", ") || "N/A";
    const status = a.status ? a.status.replace(/_/g, " ").toUpperCase() : "UNKNOWN";
    const payment = a.paymentMethod ? `${a.paymentMethod}` : "N/A";
    const price = `P${(a.totalPrice || 0).toLocaleString()}`;
    
    tableRows.push([aptDate, customer, pet, service, status, payment, price]);
  });

  autoTable(doc, {
    startY: 82,
    head: [tableColumn],
    body: tableRows,
    theme: 'striped',
    headStyles: { fillColor: [147, 51, 234] },
    alternateRowStyles: { fillColor: [243, 232, 255] }, // Purple-50
    styles: { fontSize: 8.5 }
  });

  const fileNameLabel = periodText.toLowerCase().replace(/[^a-z0-9]+/g, "_");
  doc.save(`FurpectTails_Sales_Report_${fileNameLabel}.pdf`);
};

// 2. Customer Records Report
export const generateCustomerReport = (users) => {
  const doc = initPdf("Customer Records Report");

  const activeCount = users.filter(u => u.isActive).length;
  doc.setFontSize(12);
  doc.setTextColor(50, 50, 50);
  doc.text(`Total Registered Users: ${users.length} (${activeCount} Active)`, 14, 62);

  const tableColumn = ["Name", "Email", "Phone", "Role", "Status", "Registered Pets"];
  const tableRows = [];

  users.forEach(u => {
    const name = u.name;
    const email = u.email;
    const phone = u.phone || "N/A";
    const role = u.role.toUpperCase();
    const status = u.isActive ? "Active" : "Inactive";
    const petCount = u.pets ? u.pets.length.toString() : "0";

    tableRows.push([name, email, phone, role, status, petCount]);
  });
  autoTable(doc, {
    startY: 70,
    head: [tableColumn],
    body: tableRows,
    theme: 'striped',
    headStyles: { fillColor: [147, 51, 234] },
    alternateRowStyles: { fillColor: [243, 232, 255] },
    styles: { fontSize: 9 }
  });

  doc.save("FurpectTails_Customer_Records.pdf");
};

// 3. Inventory / Services Report
export const generateInventoryReport = (services) => {
  const doc = initPdf("Services & Inventory Report");

  const activeCount = services.filter(s => s.isActive).length;
  doc.setFontSize(12);
  doc.setTextColor(50, 50, 50);
  doc.text(`Total Services: ${services.length} (${activeCount} Active)`, 14, 62);

  const tableColumn = ["Service Name", "Type", "Category", "Price", "Status"];
  const tableRows = [];

  services.forEach(s => {
    const name = s.name;
    const type = s.type;
    const category = s.category || "Any";
    const price = s.price !== undefined ? `P${s.price}` : "Varied";
    const status = s.isActive ? "Active" : "Inactive";

    tableRows.push([name, type, category, price, status]);
  });
  autoTable(doc, {
    startY: 70,
    head: [tableColumn],
    body: tableRows,
    theme: 'striped',
    headStyles: { fillColor: [147, 51, 234] },
    alternateRowStyles: { fillColor: [243, 232, 255] },
    styles: { fontSize: 9 }
  });

  doc.save("FurpectTails_Inventory_Report.pdf");
};

// 4. Audit Logs Report
export const generateAuditReport = (logs) => {
  const doc = initPdf("System Audit Logs Report");

  doc.setFontSize(12);
  doc.setTextColor(50, 50, 50);
  doc.text(`Total Audit Records: ${logs.length}`, 14, 62);

  const tableColumn = ["Timestamp", "User", "Role", "Action", "Details"];
  const tableRows = [];

  logs.forEach(log => {
    const timestamp = new Date(log.createdAt).toLocaleString();
    const user = log.userName;
    const role = log.userRole.toUpperCase();
    const action = log.action.replace(/_/g, " ");
    const details = log.details + (log.target ? ` (Target: ${log.target})` : "");

    tableRows.push([timestamp, user, role, action, details]);
  });

  autoTable(doc, {
    startY: 70,
    head: [tableColumn],
    body: tableRows,
    theme: 'striped',
    headStyles: { fillColor: [147, 51, 234] },
    alternateRowStyles: { fillColor: [243, 232, 255] },
    styles: { fontSize: 9 }
  });

  doc.save("FurpectTails_Audit_Logs.pdf");
};

// 5. Products Catalog Report
export const generateProductsReport = (products) => {
  const doc = initPdf("Products Catalog Report");

  const activeCount = products.filter(p => p.isActive !== false).length;
  doc.setFontSize(12);
  doc.setTextColor(50, 50, 50);
  doc.text(`Total Products: ${products.length} (${activeCount} Active)`, 14, 62);

  const tableColumn = ["Product Name", "Category", "Variants / Sizes", "Price Range", "Status"];
  const tableRows = [];

  products.forEach(p => {
    const name = p.name;
    const category = p.category || "General";
    const variants = p.variants && p.variants.length > 0
      ? p.variants.map(v => `${v.label} (P${v.price})`).join(", ")
      : "N/A";
    
    let priceRange = "N/A";
    if (p.variants && p.variants.length > 0) {
      const prices = p.variants.map(v => v.price);
      const min = Math.min(...prices);
      const max = Math.max(...prices);
      priceRange = min === max ? `P${min}` : `P${min} - P${max}`;
    }

    const status = p.isActive !== false ? "Active" : "Inactive";

    tableRows.push([name, category, variants, priceRange, status]);
  });

  autoTable(doc, {
    startY: 70,
    head: [tableColumn],
    body: tableRows,
    theme: 'striped',
    headStyles: { fillColor: [147, 51, 234] },
    alternateRowStyles: { fillColor: [243, 232, 255] },
    styles: { fontSize: 9 }
  });

  doc.save("FurpectTails_Products_Catalog.pdf");
};

